import { z } from 'zod';
import { getTraceId, logInfo, logWarn } from '@/lib/logger';

const COOKIE_CONSENT_STORAGE_KEY = 'dwellbot_cookie_consent';
const META_PIXEL_SCRIPT_ID = 'meta-pixel-script';
const META_PIXEL_SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';

const emptyStringAsUndefined = (value: unknown): unknown =>
  typeof value === 'string' && value.trim().length === 0 ? undefined : value;

const booleanFromEnv = z.preprocess(
  emptyStringAsUndefined,
  z
    .string()
    .optional()
    .transform((value) => value === 'true')
    .default('false')
);

const MetaPixelEnvSchema = z.object({
  browserEnabled: booleanFromEnv,
  pixelId: z.preprocess(emptyStringAsUndefined, z.string().trim().min(1).optional()),
  debug: booleanFromEnv,
  requiresCookieConsent: booleanFromEnv,
});

const CookieConsentStateSchema = z.object({
  version: z.string().min(1),
  requiresCookieConsent: z.boolean(),
  analytics: z.enum(['granted', 'denied']),
  updatedAtIso: z.string().min(1),
});

type MetaPixelPrimitive = string | number | boolean;
type MetaPixelArrayValue = Array<string | number>;
type MetaPixelValue = MetaPixelPrimitive | MetaPixelArrayValue | null | undefined;
type MetaPixelProperties = Record<string, MetaPixelValue>;
type MetaPixelBrowserConfig = z.infer<typeof MetaPixelEnvSchema>;
type CookieConsentState = z.infer<typeof CookieConsentStateSchema>;
type MetaPixelTrackOptions = {
  eventId?: string;
};
type MetaPixelQueue = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: (...args: unknown[]) => void;
  queue?: unknown[][];
  version?: string;
};

declare global {
  interface Window {
    _fbq?: MetaPixelQueue;
    fbq?: MetaPixelQueue;
  }
}

let currentConfig: MetaPixelBrowserConfig | null = null;
let initializedPixelId: string | null = null;

const readStoredJson = (storageKey: string): string | null => {
  try {
    const storedValue = window.localStorage.getItem(storageKey);
    return storedValue;
  } catch {
    return null;
  }
};

const readCookieConsentState = (): CookieConsentState | null => {
  const storedValue = readStoredJson(COOKIE_CONSENT_STORAGE_KEY);

  if (storedValue === null) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue) as unknown;
    const validatedValue = CookieConsentStateSchema.parse(parsedValue);
    return validatedValue;
  } catch {
    return null;
  }
};

const currentMetaPixelConfig = (): MetaPixelBrowserConfig => {
  if (currentConfig !== null) {
    return currentConfig;
  }

  const nextConfig = MetaPixelEnvSchema.parse({
    browserEnabled: import.meta.env.VITE_META_PIXEL_ENABLED,
    pixelId: import.meta.env.VITE_META_PIXEL_ID,
    debug: import.meta.env.VITE_META_PIXEL_DEBUG,
    requiresCookieConsent: import.meta.env.VITE_META_PIXEL_REQUIRES_COOKIE_CONSENT,
  });

  currentConfig = nextConfig;
  return currentConfig;
};

const isCookieConsentGranted = (config: MetaPixelBrowserConfig): boolean => {
  if (!config.requiresCookieConsent) {
    return true;
  }

  const cookieConsentState = readCookieConsentState();

  if (cookieConsentState === null) {
    return false;
  }

  if (!cookieConsentState.requiresCookieConsent) {
    return true;
  }

  const analyticsConsentGranted = cookieConsentState.analytics === 'granted';
  return analyticsConsentGranted;
};

const isBrowserTrackingAllowed = (config: MetaPixelBrowserConfig): boolean => {
  const browserEnabled = config.browserEnabled;

  if (!browserEnabled) {
    return false;
  }

  const hasPixelId = config.pixelId !== undefined;

  if (!hasPixelId) {
    return false;
  }

  const cookieConsentGranted = isCookieConsentGranted(config);
  return cookieConsentGranted;
};

const ensureScriptTag = (): void => {
  const existingScript = document.getElementById(META_PIXEL_SCRIPT_ID);

  if (existingScript !== null) {
    return;
  }

  const scriptElement = document.createElement('script');
  scriptElement.id = META_PIXEL_SCRIPT_ID;
  scriptElement.async = true;
  scriptElement.src = META_PIXEL_SCRIPT_URL;

  const firstScriptElement = document.getElementsByTagName('script')[0];
  const firstScriptParent = firstScriptElement?.parentNode;

  if (firstScriptParent) {
    firstScriptParent.insertBefore(scriptElement, firstScriptElement);
    return;
  }

  document.head.appendChild(scriptElement);
};

const bootstrapMetaPixelQueue = (): MetaPixelQueue => {
  const existingQueue = window.fbq;

  if (existingQueue !== undefined) {
    return existingQueue;
  }

  const nextQueue = function (...args: unknown[]): void {
    const queueRef = nextQueue;
    const queueCallMethod = queueRef.callMethod;

    if (typeof queueCallMethod === 'function') {
      queueCallMethod(...args);
      return;
    }

    const pendingQueue = queueRef.queue ?? [];
    pendingQueue.push(args);
    queueRef.queue = pendingQueue;
  } as MetaPixelQueue;

  if (window._fbq === undefined) {
    window._fbq = nextQueue;
  }

  nextQueue.push = (...args: unknown[]): void => {
    nextQueue(...args);
  };
  nextQueue.loaded = true;
  nextQueue.version = '2.0';
  nextQueue.queue = [];
  window.fbq = nextQueue;
  ensureScriptTag();

  return nextQueue;
};

const getInitializedMetaPixelQueue = (): MetaPixelQueue | null => {
  const config = currentMetaPixelConfig();
  const trackingAllowed = isBrowserTrackingAllowed(config);

  if (!trackingAllowed) {
    return null;
  }

  const pixelId = config.pixelId!;

  if (initializedPixelId === pixelId && window.fbq !== undefined) {
    return window.fbq;
  }

  const queue = bootstrapMetaPixelQueue();
  queue('init', pixelId);
  initializedPixelId = pixelId;

  return queue;
};

const sanitizeMetaPixelProperties = (
  properties: MetaPixelProperties | undefined
): MetaPixelProperties | undefined => {
  if (properties === undefined) {
    return undefined;
  }

  const sanitizedEntries = Object.entries(properties).filter(([, propertyValue]) => {
    const isDefinedValue = propertyValue !== undefined && propertyValue !== null;
    return isDefinedValue;
  });

  if (sanitizedEntries.length === 0) {
    return undefined;
  }

  const sanitizedProperties = Object.fromEntries(sanitizedEntries) as MetaPixelProperties;
  return sanitizedProperties;
};

const buildTrackOptions = (
  options: MetaPixelTrackOptions | undefined
): { eventID: string } | undefined => {
  const eventId = options?.eventId?.trim();

  if (!eventId) {
    return undefined;
  }

  const trackOptions = {
    eventID: eventId,
  };
  return trackOptions;
};

const invokeMetaPixel = (
  command: 'track' | 'trackCustom',
  eventName: string,
  properties?: MetaPixelProperties,
  options?: MetaPixelTrackOptions
): void => {
  try {
    const queue = getInitializedMetaPixelQueue();

    if (queue === null) {
      return;
    }

    const sanitizedProperties = sanitizeMetaPixelProperties(properties);
    const trackOptions = buildTrackOptions(options);

    if (sanitizedProperties === undefined && trackOptions === undefined) {
      queue(command, eventName);
      return;
    }

    if (trackOptions === undefined) {
      queue(command, eventName, sanitizedProperties);
      return;
    }

    queue(command, eventName, sanitizedProperties, trackOptions);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn('ads.meta_pixel.track_failed', 'Meta Pixel track failed', {
      trace_id: getTraceId(),
      event_name: eventName,
      command,
      error: errorMessage,
    });
  }
};

export const initMetaPixelAds = (): MetaPixelBrowserConfig => {
  const config = currentMetaPixelConfig();
  const trackingAllowed = isBrowserTrackingAllowed(config);

  logInfo('ads.meta_pixel.config', 'Meta Pixel browser config evaluated', {
    trace_id: getTraceId(),
    browser_enabled: config.browserEnabled,
    pixel_id_present: config.pixelId !== undefined,
    requires_cookie_consent: config.requiresCookieConsent,
    tracking_allowed: trackingAllowed,
    debug: config.debug,
  });

  if (trackingAllowed) {
    getInitializedMetaPixelQueue();
  }

  return config;
};

export const trackMetaPixelPageView = (properties?: MetaPixelProperties): void => {
  invokeMetaPixel('track', 'PageView', properties);
};

export const trackMetaPixelStandardEvent = (
  eventName: 'ViewContent' | 'CompleteRegistration',
  properties?: MetaPixelProperties,
  options?: MetaPixelTrackOptions
): void => {
  invokeMetaPixel('track', eventName, properties, options);
};

export const trackMetaPixelCustomEvent = (
  eventName: string,
  properties?: MetaPixelProperties,
  options?: MetaPixelTrackOptions
): void => {
  invokeMetaPixel('trackCustom', eventName, properties, options);
};

export const resetMetaPixelForTests = (): void => {
  currentConfig = null;
  initializedPixelId = null;
  delete window.fbq;
  delete window._fbq;
  const existingScript = document.getElementById(META_PIXEL_SCRIPT_ID);
  existingScript?.remove();
};
